import { makeAutoObservable } from 'mobx';

import { logger } from '@/lib/api/logger';
import { RootStoreMobx } from '@/lib/mobx/RootStoreMobx';
import { getFromWebSocket } from '@/helpers/getFromWebSocket';
import { animalsRacingDisciplines, topEventsLines } from '@/config/constants';

export class TopEventsSlider {
  /**
   * @description Root store (UI flags, etc.)
   */
  private readonly root: RootStoreMobx;

  private readonly eventsToRender = 5;

  private readonly eventsToSubscribe = 5;

  /**
   * @description Stores an AbortController instance used to cancel any in-progress
   * setSliderTopEvents execution when a new one starts (last-call-wins strategy).
   */
  private topEventsAbortController?: AbortController;

  /**
   * @description Indicates whether the data for setSliderTopEvents has been prepared.
   */
  isTopEventsDataPrepared: boolean = false;

  /**
   * @description Current list of events for the top event slider
   */
  events: EN.Event[] = [];

  /**
   * @description TopEventsSlider store
   * @param model Root store dependency
   */
  constructor(model: RootStoreMobx) {
    makeAutoObservable(this);
    this.root = model;
  }

  get topEventRefs() {
    return this.root.store.eventRefs
      .filter(({ pid, type }) => {
        const [_, did = ''] = pid.split('.');
        if (type === 1 || animalsRacingDisciplines.has(did)) {
          return false;
        }
        const [line = ''] = pid.split('.');
        return topEventsLines.has(line);
      })
      .sort((a, b) => {
        const aTopIndex = a.topIndex || 0;
        const bTopIndex = b.topIndex || 0;
        if (aTopIndex === bTopIndex) {
          return a.startTime - b.startTime;
        }

        return bTopIndex - aTopIndex;
      });
  }

  /**
   * @description Prepare top events for the slider with cancellation support.
   */
  async setSliderTopEvents({
    currentStep = 0,
    eventRefs = [],
    sliderEventIds = new Set<EN.Event['id']>(),
    sliderTopEventsCurrent = [],
    signal,
  }: {
    currentStep?: number;
    eventRefs?: EN.EventRef[];
    sliderEventIds?: Set<EN.Event['id']>;
    sliderTopEventsCurrent?: EN.Event[];
    signal?: AbortSignal;
  }): Promise<void> {
    // Bootstrap a controller if this is a top-level call (no signal passed)
    if (!signal) {
      if (this.topEventsAbortController) {
        try {
          this.topEventsAbortController.abort();
        } catch (error: unknown) {
          logger.error('topEventsAbortController error', {
            error: error as Error,
          });
        }
      }

      this.topEventsAbortController = new AbortController();
      signal = this.topEventsAbortController.signal;
    }

    if (!eventRefs?.length || signal.aborted) return;

    const nextStep = currentStep + this.eventsToSubscribe;
    const eventRefsLength = eventRefs.length;
    const topRefsLids = eventRefs.slice(currentStep, nextStep).map(({ lid }) => lid);

    if (!topRefsLids.length) return;

    const wsData = await getFromWebSocket({
      entityName: 'event',
      ids: topRefsLids,
      signal,
    });

    const eventsFromWS: EN.Event[] = wsData.resolved as EN.Event[];

    if (signal.aborted) return;

    if (!this.root?.ui?.isAmericanView) {
      eventsFromWS.forEach((event: EN.Event) => {
        if (event?.mainMarketIds?.[0] && sliderTopEventsCurrent.length < this.eventsToRender) {
          sliderEventIds.add(event.id);
          sliderTopEventsCurrent.push(event);
        }
      });
    } else {
      const matchWinnerMarketIds = eventsFromWS
        .map(({ mainMarketIds }) => mainMarketIds[0])
        .filter((id): id is string => Boolean(id));

      if (matchWinnerMarketIds.length) {
        await getFromWebSocket({
          entityName: 'market',
          ids: matchWinnerMarketIds,
          signal,
        });
        if (signal.aborted) return;
      }

      eventsFromWS.forEach((event: EN.Event) => {
        const { mainMarketIds, id } = event;
        const winnerMarketId = mainMarketIds?.[0];
        if (
          winnerMarketId &&
          sliderTopEventsCurrent.length < this.eventsToRender &&
          !sliderEventIds.has(id)
        ) {
          const market = this.root.store.markets.get(winnerMarketId) as EN.Market | undefined;

          // in AmericanView we need to show only markets with 2 outcomes
          const isChoseByOutcomesLength = this.root.ui.isAmericanView
            ? market?.outcomes?.length === 2
            : true;

          if (String(market?.interval) === '1' && isChoseByOutcomesLength) {
            sliderEventIds.add(id);
            sliderTopEventsCurrent.push(event);
          }
        }
      });
    }

    if (signal.aborted) return;

    Object.assign(this.events, sliderTopEventsCurrent);

    if (sliderTopEventsCurrent.length < this.eventsToRender && currentStep < eventRefsLength) {
      await this.setSliderTopEvents({
        currentStep: nextStep,
        eventRefs,
        signal,
        sliderEventIds,
        sliderTopEventsCurrent,
      });
    } else {
      if (!signal.aborted) {
        this.isTopEventsDataPrepared = true;
      }
    }
  }
}
